import React, { createContext, useContext } from 'react';
import {
  updateDoorCrewCanvasser,
  deleteDoorCrewCanvasser,
} from '../../store/api/calls/doors';
import { DoorCrew, DoorCrewSeat, SeatRole, Turf, Canvasser, TurfCode } from './types';

export interface DoorCrewCardContextValue {
  crew: DoorCrew;
  canvassers: Canvasser[];
  turfCodes: TurfCode[];
  /** Patch a seat. Calls updateDoorCrewCanvasser then onCanvasserUpdate. */
  onUpdateSeat: (
    seatId: number,
    changes: Partial<Pick<DoorCrewSeat, 'canvasser' | 'comments' | 'goal' | 'assignment_placeholder_note'> & { roles: SeatRole[] }>
  ) => Promise<void>;
  /** Delete a seat (and its crew if it's the last seat). */
  onDeleteSeat: (seatId: number, turfId: number | undefined, canvasserId: number | null) => void;
  /** Unassign a canvasser from a seat (set canvasser → null via patch). */
  onUnassignSeat: (crewCanvasserId: number) => void;
  /** Open the turf note modal for a turf in this crew. */
  onOpenNoteModal: (turf: Turf, crewId: number) => void;
}

const DoorCrewCardContext = createContext<DoorCrewCardContextValue | null>(null);

export function useDoorCrewCard(): DoorCrewCardContextValue {
  const ctx = useContext(DoorCrewCardContext);
  if (!ctx) {
    throw new Error('useDoorCrewCard must be used inside DoorCrewCardContext.Provider');
  }
  return ctx;
}

export interface DoorCrewCardProviderProps {
  crew: DoorCrew;
  canvassers: Canvasser[];
  turfCodes: TurfCode[];
  onCanvasserUpdate: () => void;
  onDeleteCrew: () => void;
  onOpenNoteModal: (turf: Turf, crewId: number) => void;
  children: React.ReactNode;
}

export function DoorCrewCardProvider({
  crew,
  canvassers,
  turfCodes,
  onCanvasserUpdate,
  onDeleteCrew,
  onOpenNoteModal,
  children,
}: DoorCrewCardProviderProps) {
  const onUpdateSeat: DoorCrewCardContextValue['onUpdateSeat'] = async (seatId, changes) => {
    await updateDoorCrewCanvasser(seatId, changes);
    onCanvasserUpdate();
  };

  const onDeleteSeat: DoorCrewCardContextValue['onDeleteSeat'] = (seatId, _turfId, _canvasserId) => {
    if (crew.seats.length <= 1) {
      onDeleteCrew();
    } else {
      deleteDoorCrewCanvasser(seatId).then(onCanvasserUpdate);
    }
  };

  const onUnassignSeat: DoorCrewCardContextValue['onUnassignSeat'] = (crewCanvasserId) => {
    updateDoorCrewCanvasser(crewCanvasserId, { canvasser: null }).then(onCanvasserUpdate);
  };

  const value: DoorCrewCardContextValue = {
    crew,
    canvassers,
    turfCodes,
    onUpdateSeat,
    onDeleteSeat,
    onUnassignSeat,
    onOpenNoteModal,
  };

  return (
    <DoorCrewCardContext.Provider value={value}>
      {children}
    </DoorCrewCardContext.Provider>
  );
}

export default DoorCrewCardContext;
